import type { ReactNode } from 'react';
import { Activity, Eye, Newspaper, Users } from 'lucide-react';

import { getLocale } from '../../../i18n';
import { count, score } from '../../../chrome/measure/format';
import { Absent, NcsValue } from '../../../chrome/measure';
import { StatCard, StatGrid } from '../../../chrome/page';
import { Chip } from '../../../ui/chip';
import { deltaOf, type Delta, type DeltaKind } from './figures';
import { ov } from './messages';
import type { Overview } from '../api/schemas';

export function NoReading(): ReactNode {
  return <Absent reason={{ text: ov('absent.reading') }} />;
}

const CHIP_TONE: Record<Delta['trend'], 'positive' | 'negative' | 'neutral'> = {
  up: 'positive',
  down: 'negative',
  flat: 'neutral',
};

function DeltaChip(props: { delta: Delta }): ReactNode {
  return (
    <Chip tone={CHIP_TONE[props.delta.trend]} size="sm">
      {props.delta.text}
    </Chip>
  );
}

/** `shown` is `null` when the figure has no reading to show, and then it has
 *  no delta either: a change against nothing is not zero. */
function Figure(props: {
  label: string;
  icon: ReactNode;
  figure: { value: number | null; previous: number | null };
  kind: DeltaKind;
  shown: ReactNode | null;
  formatPrevious: (previous: number) => string;
  sparkline?: readonly number[];
}): ReactNode {
  const { figure } = props;
  const delta = props.shown === null ? null : deltaOf(figure, props.kind, getLocale());

  return (
    <StatCard
      label={props.label}
      aside={props.icon}
      value={props.shown ?? <NoReading />}
      chip={delta !== null ? <DeltaChip delta={delta} /> : undefined}
      trend={delta?.trend}
      sparkline={props.sparkline}
      caption={
        delta !== null && figure.previous !== null
          ? ov('delta.previous', { previous: props.formatPrevious(figure.previous) })
          : undefined
      }
    />
  );
}

/** The score is shown only with the sample and the interval it was read at. */
function sentimentShown(sentiment: Overview['sentiment']): ReactNode | null {
  const { value, n, confidence_interval: interval } = sentiment;
  if (value === null || n == null || interval == null) return null;
  return <NcsValue score={value} n={n} interval={interval} />;
}

export function MetricsGrid(props: { reading: Overview }): ReactNode {
  const { reach, posts, active_accounts: accounts, sentiment, movement } = props.reading;
  const locale = getLocale();
  const counted = (value: number | null): string | null =>
    value === null ? null : count(value, locale);
  // `movement` is the only time series the query returns, and it is reach's own
  // (`movement.value` renders as "Reach"): the other three figures have no
  // series to chart until the route carries one, per ruling-7.
  const reachSparkline = movement
    .map((bucket) => bucket.value)
    .filter((value): value is number => value !== null);

  const iconClass = 'size-4 text-muted-foreground';

  return (
    <StatGrid>
      <Figure
        label={ov('figure.reach')}
        icon={<Eye aria-hidden className={iconClass} />}
        figure={reach}
        kind="percent"
        shown={counted(reach.value)}
        formatPrevious={(previous) => count(previous, locale)}
        sparkline={reachSparkline.length > 0 ? reachSparkline : undefined}
      />
      <Figure
        label={ov('figure.posts')}
        icon={<Newspaper aria-hidden className={iconClass} />}
        figure={posts}
        kind="count"
        shown={counted(posts.value)}
        formatPrevious={(previous) => count(previous, locale)}
      />
      <Figure
        label={ov('figure.accounts')}
        icon={<Users aria-hidden className={iconClass} />}
        figure={accounts}
        kind="count"
        shown={counted(accounts.value)}
        formatPrevious={(previous) => count(previous, locale)}
      />
      <Figure
        label={ov('figure.sentiment')}
        icon={<Activity aria-hidden className={iconClass} />}
        figure={sentiment}
        kind="points"
        shown={sentimentShown(sentiment)}
        formatPrevious={(previous) => score(previous, locale)}
      />
    </StatGrid>
  );
}
